export const OFFER_PARTS = ["A", "B", "C"] as const;
export type OfferPart = typeof OFFER_PARTS[number];
export type OfferPartSettings = Record<OfferPart, { title: string; included: boolean; description?: string }>;
export const PART_C_NOTE = "Diese Arbeiten können wahlweise von uns, in Eigenleistung oder durch ein vom Bauherrn beauftragtes Unternehmen ausgeführt werden. Bei Fremdvergabe sind die Flächen bis zum vereinbarten Termin vollständig frei, tragfähig und gereinigt zu übergeben. Für Leistungen Dritter übernehmen wir keine Gewährleistung. Mehraufwand durch nicht fachgerecht vorbereitete Untergründe wird nach Aufwand verrechnet.";
export const DEFAULT_PARTS: OfferPartSettings = {
  A: { title: "Teil A – Wärmedämmung (förderfähig)", included: true },
  B: { title: "Teil B – Zusatzleistungen (optional)", included: true },
  C: { title: "Teil C – Vorleistungen (Bauherr oder Dritte)", included: true, description: PART_C_NOTE },
};
export const OPTIONAL_PART_NOTE = "Optional – nicht in der Gesamtsumme enthalten";
export const offerPart = (part?: string): OfferPart => part === "B" || part === "C" ? part : "A";
export function normalizePartSettings(settings?: Partial<OfferPartSettings>): OfferPartSettings {
  return Object.fromEntries(OFFER_PARTS.map((part) => {
    // Missing legacy C text keeps its previous wording; an explicitly empty
    // description means the user removed it and must stay empty after reload.
    const description = settings?.[part]?.description ?? DEFAULT_PARTS[part].description;
    return [part, {
      title: settings?.[part]?.title?.trim() || DEFAULT_PARTS[part].title,
      included: settings?.[part]?.included !== false,
      ...(description === undefined ? {} : { description }),
    }];
  })) as OfferPartSettings;
}
export interface PartRow { teil?: OfferPart; total: number; variant?: boolean }
/** Both renderers use the same rows, default assignment and variant exclusion.
 * Keep full precision until display, matching the existing offer arithmetic. */
export function calculateOfferParts<T extends PartRow>(rows: T[], settings?: Partial<OfferPartSettings>) {
  const config = normalizePartSettings(settings);
  const parts = OFFER_PARTS.map((id) => {
    const positions = rows.filter((row) => offerPart(row.teil) === id);
    return { id, ...config[id], positions,
      subtotal: positions.reduce((sum, row) => row.variant ? sum : sum + row.total, 0) };
  }).filter((part) => part.positions.length > 0);
  const subtotal = parts.reduce((sum, part) => part.included ? sum + part.subtotal : sum, 0);
  const tax = subtotal * 0.20;
  // A-only offers retain the original layout, even when A is optional.
  const grouped = parts.some((part) => part.id !== "A");
  return { parts, subtotal, tax, total: subtotal + tax, grouped };
}
